<script setup lang="ts">
import { useFloating } from '@floating-ui/vue';
import {
  onMounted,
  onUnmounted,
  ref,
  toRefs,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarOverlayCard } from '@nora-monorepo/grammar-ui';
import type { UserResultDTO } from '@nora-monorepo/nora-api-client';

import AvatarImage from '#ui-shared/components/avatar-image/AvatarImage.vue';
import { getInitials } from '#ui-shared/utils/get-initials';

interface UserDetailsProps {
  user: UserResultDTO;
  triggerElement: HTMLElement;
  showDelay?: number;
}

interface DisplayProperties {
  callSign?: string | null;
  name?: string | null;
  username?: string | null;
  email?: string | null;
}

const props = withDefaults(defineProps<UserDetailsProps>(), {
  showDelay: 500,
});
const { t } = useI18n();
const { triggerElement } = toRefs(props);

const userDetailsPanel = ref();
const showPanel = ref(false);
const showTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

function getUserNameProperty(user: UserResultDTO) {
  if (!user.firstName && !user.lastName) {
    return null;
  }

  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }

  return user.firstName || user.lastName;
}

const userInfo: DisplayProperties = {
  callSign: props.user.customCallSign ?? props.user.callSign ?? '',
  name: getUserNameProperty(props.user),
  username: props.user.username,
  email: props.user.email,
};

const displayKeys: (keyof DisplayProperties)[] = [
  'callSign',
  'name',
  'username',
  'email',
];

async function init() {
  if (!triggerElement.value) {
    return;
  }

  triggerElement.value.addEventListener('mouseenter', () => {
    if (showTimeout.value) {
      clearTimeout(showTimeout.value);
    }

    showTimeout.value = setTimeout(() => {
      showPanel.value = true;
    }, props.showDelay);
  });

  triggerElement.value.addEventListener('mouseleave', () => {
    if (showTimeout.value) {
      clearTimeout(showTimeout.value);

      showTimeout.value = null;
    }

    showPanel.value = false;
  });
}

onMounted(() => {
  init();
});

onUnmounted(() => {
  if (showTimeout.value) {
    clearTimeout(showTimeout.value);
  }
});

const { floatingStyles } = useFloating(triggerElement, userDetailsPanel, {
  placement: 'top',
  strategy: 'fixed',
});
</script>

<template>
  <GrammarOverlayCard
    v-if="showPanel"
    ref="userDetailsPanel"
    class="h-auto text-light-label-primary dark:text-dark-label-primary z-50"
    :style="{ ...floatingStyles }"
  >
    <div class="flex items-center p-3">
      <AvatarImage :id="user.id" :text="getInitials(user)" />
      <div class="pl-3">
        <p
          v-for="displayKey of displayKeys"
          v-show="userInfo[displayKey]"
          :key="displayKey"
          class="text-xs leading-4"
        >
          <span>{{ t(`hoverResource.${displayKey}`) }}</span>:&nbsp;<span class="font-semibold">{{ userInfo[displayKey] }}</span>
        </p>
      </div>
    </div>
  </GrammarOverlayCard>
</template>
